// «Оценка готовности» (дашборд заказчика, 2026-07-03): светофор по худшей
// проверке + карточка на каждую проверку с ДВУМЯ слоями (находки движка +
// замечания) + дельта к предыдущей редакции + свежесть. Клик по карточке — переход
// на вкладку проверки. Данные: GET /projects/{id}/readiness-summary.
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, ShieldAlert, ShieldQuestion, BookOpenCheck, Landmark, ClipboardList, FileSpreadsheet, ArrowRight, Send, MessageCircleQuestion, PlayCircle, Lock, FileWarning } from 'lucide-react';
import { api } from '@/api/client';
import { useAuth } from '@/state/auth';
import { toast } from '@/state/toast';
import { Button } from './Button';
import { PromoteFindingsModal } from './PromoteFindingsModal';
import { AskSpecialistModal } from './AskSpecialistModal';
import { ConclusionPanel } from './ConclusionPanel';

interface Summary {
  verdict: { level: 'ready' | 'attention' | 'not_ready' | 'unknown'; reasons: string[] };
  completeness: {
    has_result: boolean; total?: number; absent?: number; present?: number;
    not_applicable?: number; promoted?: number; closed?: number;
    finished_at?: string | null; stale?: boolean;
  };
  norms: {
    has_result: boolean; refs?: number; problems?: number;
    breakdown?: Record<string, number>; promoted?: number; closed?: number;
    finished_at?: string | null; prev_problems?: number | null; stale?: boolean;
  };
  decisions: { count: number; promoted: number; closed: number };
  tep?: {
    has_result: boolean; total?: number; mismatch?: number; gap?: number; ok?: number;
    problems?: number; promoted?: number; finished_at?: string | null;
    prev_problems?: number | null; stale?: boolean;
  };
  task_compliance?: {
    has_result: boolean; total?: number; mismatch?: number; not_covered?: number;
    partial?: number; manual?: number; problems?: number; promoted?: number;
    finished_at?: string | null; prev_problems?: number | null; stale?: boolean;
  };
  acceptance_remarks: { open: number; closed: number };
  // Замечания стадии «экспертиза»: сюда попадают и находки, отправленные
  // проектировщику, и замечания по СОСТАВУ ПД — раздел не представлен.
  // Последние не видны ни в одной проверке: требования отсутствующего
  // раздела помечены «неприменимо» и в пробелы не попадают.
  expertise_remarks?: { open: number; closed: number };
}

const VERDICT_META = {
  ready:     { icon: ShieldCheck,    color: '#10B981', bg: 'rgba(16,185,129,.07)',  border: 'rgba(16,185,129,.25)',  title: 'Существенных препятствий не найдено' },
  attention: { icon: ShieldAlert,    color: '#F59E0B', bg: 'rgba(245,158,11,.07)',  border: 'rgba(245,158,11,.3)',   title: 'Требует внимания перед подачей' },
  not_ready: { icon: ShieldAlert,    color: '#EF4444', bg: 'rgba(239,68,68,.06)',   border: 'rgba(239,68,68,.3)',    title: 'Не готова к подаче' },
  unknown:   { icon: ShieldQuestion, color: '#6B7280', bg: 'rgba(107,114,128,.06)', border: '#E5E7EB',               title: 'Проверки ещё не запускались' },
} as const;

// Разбивка норм: статус → цвет мини-бара (порядок = порядок сегментов).
const NORM_SEGMENTS: [string, string, string][] = [
  ['cancelled', '#EF4444', 'отменены'],
  ['replaced',  '#F59E0B', 'заменены'],
  ['not_found', '#7C3AED', 'не существуют'],
  ['undated',   '#38BDF8', 'без редакции'],
  ['subtitle',  '#9CA3AF', 'ист. названия'],
  ['unknown',   '#D1D5DB', 'не проверено'],
  ['active',    '#10B981', 'действуют'],
];

export function ReadinessOverview({ projectId, onNavigate, onGoToRemarks, tariff = null, onPay }: {
  projectId: string;
  onNavigate: (view: string) => void;
  onGoToRemarks?: () => void;
  tariff?: string | null;      // 15.F: null → находки под замком, показываем CTA оплаты
  onPay?: () => void;
}) {
  const qc = useQueryClient();
  const [polling, setPolling] = useState(false); // после «Проверить готовность» опрашиваем сводку
  const { data } = useQuery<Summary>({
    queryKey: ['readiness-summary', projectId],
    queryFn: () => api.get<Summary>(`/projects/${projectId}/readiness-summary`),
    staleTime: 30_000,
    refetchInterval: polling ? 8000 : false,
  });
  // 15.F «Проверить готовность»: одна кнопка запускает все 4 проверки (бесплатно).
  const runAll = useMutation({
    mutationFn: () => api.post(`/projects/${projectId}/run-all-checks`, {}),
    onSuccess: () => {
      setPolling(true);
      toast.success('Проверки запущены — идут в фоне, обновляется автоматически');
      qc.invalidateQueries({ queryKey: ['readiness-summary', projectId] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Не удалось запустить проверки'),
  });
  // Останавливаем поллинг, когда обе документные проверки завершились.
  useEffect(() => {
    if (polling && data && data.completeness.has_result && data.norms.has_result) setPolling(false);
  }, [polling, data]);
  // 12.3: сколько находок можно передать проектировщику (hard — по умолчанию).
  const promotableQ = useQuery<{ hard_total: number; soft_total: number }>({
    queryKey: ['promotable-findings', projectId],
    queryFn: () => api.get<{ hard_total: number; soft_total: number }>(`/projects/${projectId}/promotable-findings`),
    staleTime: 30_000,
  });
  // 15.F: открыты ли находки. Внутренние роли (админ/куратор/эксперт/ГИП) не
  // платят — им открыто всегда, даже когда админ смотрит «глазами заказчика».
  // Оплата решает только для НАСТОЯЩЕГО заказчика.
  // NB: хук обязан вызываться ДО раннего return ниже (правила хуков React).
  const isRealCustomer = useAuth((st) => (st.user?.default_role || '') === 'customer');
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [askOpen, setAskOpen] = useState(false); // 15.C «Спросить специалиста»

  if (!data) return <div className="text-muted" style={{ padding: 32, fontSize: 13 }}>Загружаю сводку…</div>;
  const promotableN = promotableQ.data?.hard_total ?? 0;
  const canPromote = data.verdict.level !== 'ready' && data.verdict.level !== 'unknown' && (promotableN + (promotableQ.data?.soft_total ?? 0)) > 0;

  const v = VERDICT_META[data.verdict.level] ?? VERDICT_META.unknown;
  const VIcon = v.icon;
  const paid = tariff === 'express' || tariff === 'full' || !isRealCustomer;
  const notRun = data.verdict.level === 'unknown';          // проверки ещё не гоняли
  const c = data.completeness;
  const n = data.norms;
  const d = data.decisions;
  const delta = n.has_result && n.prev_problems != null ? (n.problems! - n.prev_problems) : null;
  const expOpen = data.expertise_remarks?.open ?? 0;

  return (
    <div>
      {/* ── Сводное заключение специалиста (мигр. 075) ──
           Стоит ПЕРЕД светофором: светофор считает по сырым цифрам движка,
           а заключение говорит, что из них подтвердилось и чего не хватает. */}
      <ConclusionPanel projectId={projectId} onPay={onPay} />

      {/* ── Светофор ── */}
      <div className="rounded-card" style={{ padding: '20px 24px', background: v.bg, border: `1px solid ${v.border}`, marginBottom: 20 }}>
        <div className="flex items-start" style={{ gap: 14 }}>
          <VIcon size={30} stroke={v.color} />
          <div>
            <h3 className="font-heading" style={{ fontSize: 19, fontWeight: 650, color: v.color }}>{v.title}</h3>
            {data.verdict.reasons.length > 0 && (
              /* 11.6/F-12: причины списком — раньше склеивались через « · », а
                 внутри причин свои запятые, читалось как каша. */
              <ul className="text-ink" style={{ fontSize: 13, marginTop: 6, lineHeight: 1.5, paddingLeft: 18, listStyle: 'disc' }}>
                {data.verdict.reasons.map((r, i) => <li key={i} style={{ marginTop: i ? 3 : 0 }}>{r}</li>)}
              </ul>
            )}
          </div>
        </div>
        {/* 12.3: находки → замечания проектировщику. 15.C: спросить живого эксперта
            (ненавязчиво — одна кнопка после вердикта, не в каждой находке). */}
        <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${v.border}`, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* 15.F: одна кнопка запускает все 4 проверки (бесплатно). */}
          {notRun && (
            <Button variant="primary" size="sm" icon={<PlayCircle size={16} />} onClick={() => runAll.mutate()} disabled={runAll.isPending}>
              {runAll.isPending ? 'Запускаю…' : 'Проверить готовность'}
            </Button>
          )}
          {polling && (
            <span style={{ fontSize: 12.5, color: '#B45309', fontWeight: 600 }}>
              Проверки идут в фоне — цифры обновятся автоматически…
            </span>
          )}
          {/* Сформировать замечания — только когда находки открыты (оплачено). */}
          {paid && canPromote && (
            <Button variant="primary" size="sm" icon={<Send size={15} />} onClick={() => setPromoteOpen(true)}>
              Сформировать замечания проектировщику{promotableN > 0 ? ` (${promotableN})` : ''}
            </Button>
          )}
          <Button variant="secondary" size="sm" icon={<MessageCircleQuestion size={15} />} onClick={() => setAskOpen(true)}>
            Спросить специалиста
          </Button>
        </div>
      </div>

      {/* 15.F: проверки пройдены, но не оплачено → CTA открыть детали находок. */}
      {!notRun && !paid && (
        <div className="rounded-card" style={{ padding: '16px 20px', background: 'rgba(245,158,11,.08)', border: '1px solid rgba(245,158,11,.35)', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <Lock size={20} stroke="#B45309" style={{ flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 220 }}>
            <div className="text-ink" style={{ fontSize: 14, fontWeight: 650 }}>Проверки пройдены — цифры выше</div>
            <div className="text-muted" style={{ fontSize: 12.5, marginTop: 2, lineHeight: 1.5 }}>
              Оплатите тариф, чтобы открыть детали каждой находки: где именно, по какому требованию/норме и с цитатой из документа.
            </div>
          </div>
          <button type="button" onClick={onPay} className="rounded-pill"
            style={{ padding: '10px 22px', fontSize: 14, fontWeight: 700, background: '#F59E0B', color: '#1F1300', border: 'none', cursor: 'pointer', flexShrink: 0 }}>
            Оплатить
          </button>
        </div>
      )}

      {promoteOpen && (
        <PromoteFindingsModal projectId={projectId} onClose={() => setPromoteOpen(false)} onGoToRemarks={onGoToRemarks} />
      )}
      {askOpen && <AskSpecialistModal projectId={projectId} onClose={() => setAskOpen(false)} />}

      {/* ── Карточки проверок ── */}
      <div className="grid grid-cols-2" style={{ gap: 16 }}>
        {/* Состав документации (Block A) */}
        <CheckCard
          icon={ClipboardList}
          title="Состав документации"
          ok={c.has_result && c.absent === 0}
          hasResult={c.has_result}
          onOpen={() => onNavigate('compliance')}
          headline={c.has_result ? (c.absent === 0 ? 'Пробелов нет' : `Пробелов: ${c.absent}`) : 'Проверка не запускалась'}
          finishedAt={c.finished_at} stale={c.stale}
          metrics={c.has_result ? [
            { label: 'Раскрыто', value: `${c.present ?? 0} из ${c.total ?? 0}` },
            { label: 'Пробелов', value: String(c.absent ?? 0), color: (c.absent ?? 0) > 0 ? '#EF4444' : '#10B981' },
            /* Неприменимое НЕ прячем: на площадочных объектах без зданий это
               большая часть ПП-87, и без этой цифры «пробелов 4» читается как
               «проверено почти всё», хотя проверено меньше. */
            { label: 'Неприменимо к объекту', value: String(c.not_applicable ?? 0), color: '#6B7280' },
          ] : []}
        />

        {/* Актуальность норм */}
        <CheckCard
          icon={BookOpenCheck}
          title="Актуальность норм"
          ok={n.has_result && n.problems === 0}
          hasResult={n.has_result}
          onOpen={() => onNavigate('norms')}
          headline={n.has_result ? (n.problems === 0 ? 'Все ссылки в порядке' : `Проблемных ссылок: ${n.problems}`) : 'Проверка не запускалась'}
          finishedAt={n.finished_at} stale={n.stale}
          deltaText={delta != null && delta !== 0 ? `${delta > 0 ? '+' : ''}${delta} к предыдущей редакции` : undefined}
          deltaGood={delta != null && delta < 0}
          metrics={n.has_result ? [
            { label: 'Норм в документации', value: String(n.refs ?? 0) },
            { label: 'Проблемных', value: String(n.problems ?? 0), color: (n.problems ?? 0) > 0 ? '#EF4444' : '#10B981' },
            { label: 'В замечаниях / закрыто', value: `${n.promoted ?? 0} / ${n.closed ?? 0}` },
          ] : []}
          extra={n.has_result && n.breakdown ? <NormBar breakdown={n.breakdown} total={n.refs ?? 0} /> : undefined}
        />

        {/* Проектное решение */}
        <CheckCard
          icon={Landmark}
          title="Проектное решение"
          ok={n.has_result && d.count === 0}
          hasResult={n.has_result}
          onOpen={() => onNavigate('decisions')}
          headline={n.has_result
            ? (d.count === 0 ? 'Затронутых решений нет' : `Решений на устаревших обоснованиях: ${d.count}`)
            : 'Строится при проверке норм'}
          finishedAt={n.finished_at} stale={n.stale}
          metrics={n.has_result ? [
            { label: 'Затронуто решений', value: String(d.count), color: d.count > 0 ? '#EF4444' : '#10B981' },
            { label: 'В замечаниях', value: String(d.promoted) },
            { label: 'Закрыто', value: String(d.closed), color: '#10B981' },
          ] : []}
        />

        {/* Соответствие заданию: попунктная проверка (8F) приоритетна; ТЭП — дополняет */}
        <CheckCard
          icon={FileSpreadsheet}
          title="Соответствие заданию"
          ok={data.task_compliance?.has_result
            ? (data.task_compliance.problems ?? 0) === 0 && (data.tep?.problems ?? 0) === 0
            : (data.tep?.has_result ? (data.tep.problems ?? 0) === 0 : undefined)}
          hasResult={!!(data.task_compliance?.has_result || data.tep?.has_result)}
          onOpen={() => onNavigate('tep')}
          headline={data.task_compliance?.has_result
            ? ((data.task_compliance.problems ?? 0) === 0
                ? 'Пункты задания отражены'
                : `Отступлений: ${data.task_compliance.mismatch ?? 0} · не отражено: ${data.task_compliance.not_covered ?? 0} · частично: ${data.task_compliance.partial ?? 0}`)
            : data.tep?.has_result
              ? `Сверка ТЭП: расхождений ${data.tep.mismatch ?? 0}, пробелов ${data.tep.gap ?? 0}`
              : 'Проверка не запускалась — откройте вкладку'}
          finishedAt={data.task_compliance?.finished_at || data.tep?.finished_at}
          stale={data.task_compliance?.stale || data.tep?.stale}
          deltaText={data.task_compliance?.has_result && data.task_compliance.prev_problems != null
              && (data.task_compliance.problems! - data.task_compliance.prev_problems) !== 0
            ? `${data.task_compliance.problems! - data.task_compliance.prev_problems > 0 ? '+' : ''}${data.task_compliance.problems! - data.task_compliance.prev_problems} к предыдущей редакции`
            : undefined}
          deltaGood={data.task_compliance?.has_result && data.task_compliance.prev_problems != null
            && data.task_compliance.problems! < data.task_compliance.prev_problems}
          metrics={data.task_compliance?.has_result ? [
            { label: 'Пунктов задания', value: String(data.task_compliance.total ?? 0) },
            { label: 'Проблемных', value: String(data.task_compliance.problems ?? 0), color: (data.task_compliance.problems ?? 0) > 0 ? '#EF4444' : '#10B981' },
            { label: 'ТЭП: расхожд./пробелы', value: `${data.tep?.mismatch ?? 0} / ${data.tep?.gap ?? 0}`, color: (data.tep?.problems ?? 0) > 0 ? '#F59E0B' : '#10B981' },
          ] : (data.tep?.has_result ? [
            { label: 'Показателей ТЭП', value: String(data.tep.total ?? 0) },
            { label: 'Проблемных', value: String(data.tep.problems ?? 0), color: (data.tep.problems ?? 0) > 0 ? '#EF4444' : '#10B981' },
            { label: 'В замечаниях', value: String(data.tep.promoted ?? 0) },
          ] : [])}
        />
      </div>

      {/* Замечания, отправленные проектировщику. Сюда попадает то, что заказчик
          сам перенёс из находок кнопкой «Сформировать замечания». Результаты
          НАШЕЙ проверки в ленту замечаний заказчика не кладутся (решение
          владельца 31.08.2026) — они живут внутри самих проверок. */}
      {expOpen > 0 && (
        <div className="rounded-card" style={{ marginTop: 20, padding: '16px 20px', background: '#FFF', border: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <FileWarning size={22} stroke="#B45309" style={{ flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 240 }}>
            <div className="text-ink" style={{ fontSize: 14, fontWeight: 650 }}>
              Замечаний отправлено проектировщику: {expOpen}
            </div>
            <div className="text-muted" style={{ fontSize: 12.5, marginTop: 3, lineHeight: 1.5 }}>
              Находки, которые вы перенесли в работу проектировщику. Открыть — чтобы
              посмотреть статус и ответы.
            </div>
          </div>
          {onGoToRemarks && (
            <Button variant="secondary" size="sm" icon={<ArrowRight size={15} />} onClick={onGoToRemarks}>
              Открыть замечания
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function CheckCard({ icon: Icon, title, ok, hasResult, headline, metrics, onOpen, finishedAt, stale, deltaText, deltaGood, extra }: {
  icon: typeof ClipboardList;
  title: string;
  ok?: boolean;
  hasResult: boolean;
  headline: string;
  metrics: { label: string; value: string; color?: string }[];
  onOpen: () => void;
  finishedAt?: string | null;
  stale?: boolean;
  deltaText?: string;
  deltaGood?: boolean;
  extra?: React.ReactNode;
}) {
  const tone = !hasResult ? '#9CA3AF' : ok === undefined ? '#6B7280' : ok ? '#10B981' : '#EF4444';
  return (
    <button
      type="button"
      onClick={onOpen}
      className="rounded-card"
      style={{ padding: 20, background: '#FFF', border: '1px solid #E5E7EB', textAlign: 'left', cursor: 'pointer', display: 'block', width: '100%' }}
    >
      <div className="flex items-start" style={{ gap: 12 }}>
        <Icon size={22} stroke={tone} style={{ marginTop: 2 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="flex items-center" style={{ gap: 8 }}>
            <span className="text-muted uppercase tracking-wider" style={{ fontSize: 11, fontWeight: 600 }}>{title}</span>
            <ArrowRight size={13} stroke="#9CA3AF" style={{ marginLeft: 'auto', flexShrink: 0 }} />
          </div>
          <div className="font-heading" style={{ fontSize: 16, fontWeight: 600, color: tone, marginTop: 3 }}>
            {headline}
            {deltaText && (
              <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 600, color: deltaGood ? '#10B981' : '#EF4444' }}>
                {deltaText}
              </span>
            )}
          </div>
        </div>
      </div>
      {extra}
      {metrics.length > 0 && (
        <div className="border-t grid grid-cols-3" style={{ gap: 12, marginTop: 14, paddingTop: 12, borderColor: '#F3F4F6' }}>
          {metrics.map((m) => (
            <div key={m.label}>
              <div className="text-muted" style={{ fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.02em' }}>{m.label}</div>
              <div className="font-heading tabular" style={{ fontSize: 18, fontWeight: 600, color: m.color || '#0A1628', marginTop: 2 }}>{m.value}</div>
            </div>
          ))}
        </div>
      )}
      <div className="text-muted" style={{ fontSize: 11, marginTop: 10 }}>
        {finishedAt ? `проверено ${new Date(finishedAt).toLocaleString('ru-RU')}` : ''}
        {stale && <span style={{ color: '#B45309', fontWeight: 600 }}> · документы менялись после проверки — перепроверьте</span>}
      </div>
    </button>
  );
}

function NormBar({ breakdown, total }: { breakdown: Record<string, number>; total: number }) {
  if (!total) return null;
  return (
    <div style={{ marginTop: 12 }}>
      <div className="flex" style={{ height: 8, borderRadius: 5, overflow: 'hidden', background: '#F3F4F6' }}>
        {NORM_SEGMENTS.map(([key, color]) => {
          const nSeg = breakdown[key] || 0;
          if (!nSeg) return null;
          return <div key={key} style={{ width: `${(100 * nSeg) / total}%`, background: color }} title={`${key}: ${nSeg}`} />;
        })}
      </div>
      <div className="flex text-muted" style={{ gap: 10, marginTop: 6, fontSize: 10.5, flexWrap: 'wrap' }}>
        {NORM_SEGMENTS.filter(([k]) => breakdown[k]).map(([key, color, label]) => (
          <span key={key} className="flex items-center" style={{ gap: 3 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: color, display: 'inline-block' }} />
            {label}: {breakdown[key]}
          </span>
        ))}
      </div>
    </div>
  );
}
